import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

interface IPushViewContext {
  pushIds: string[];
  setPushIds: (ids: string[]) => void;
}

const STORAGE_KEY = 'pushViewIds';

export const PushViewContext = createContext<IPushViewContext>({
  pushIds: [],
  setPushIds: () => {},
});

/**
 * Provider para gerenciar estado de pushes para monitoramento.
 * 
 * Mantém uma lista de IDs de pushes para monitoramento.
 * Os dados são persistidos no localStorage para sobreviver a navegações.
 */
export const PushViewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pushIds, setPushIdsState] = useState<string[]>([]);

  /**
   * Carrega os IDs do localStorage ao montar o componente.
   */
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setPushIdsState(parsed);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar pushIds do localStorage:', error);
    }
  }, []);

  /**
   * Define os IDs e salva no localStorage.
   */
  const setPushIds = useCallback((ids: string[]) => {
    setPushIdsState(ids);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    } catch (error) {
      console.error('Erro ao salvar pushIds no localStorage:', error);
    }
  }, []);

  return (
    <PushViewContext.Provider
      value={{
        pushIds,
        setPushIds,
      }}
    >
      {children}
    </PushViewContext.Provider>
  );
};

/**
 * Hook para acessar o contexto de pushes.
 * 
 * @returns Contexto de pushes
 */
export const usePushViewContext = (): IPushViewContext => {
  const context = useContext(PushViewContext);
  if (!context) {
    throw new Error('usePushViewContext must be used within PushViewProvider');
  }
  return context;
};
